import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Безопасное преобразование значения из API в строку для UI.
 * Избегает String(exotic), который может бросить «Cannot convert object to primitive value».
 */
export function toUiString(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "string") return value
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  if (typeof value === "boolean") return value ? "true" : "false"
  if (typeof value === "object") {
    const o = value as Record<string, unknown>
    const nested =
      typeof o.ru === "string"
        ? o.ru
        : typeof o.en === "string"
          ? o.en
          : typeof o.name === "string"
            ? o.name
            : typeof o.text === "string"
              ? o.text
              : null
    if (nested) return nested
    try {
      return JSON.stringify(value)
    } catch {
      return ""
    }
  }
  try {
    return String(value as string | number | bigint | boolean | symbol)
  } catch {
    return ""
  }
}

/**
 * Совместимость с legacy-форматом Circle:
 * если в данных встречается Point + radius, конвертируем в Polygon.
 * Для текущей модели (PostGIS) геометрия круга обычно уже хранится как Polygon,
 * поэтому в большинстве случаев функция вернет входные данные без изменений.
 */
export function convertCircleToPolygon(circleGeoJSON: any): any {
  if (!circleGeoJSON) return circleGeoJSON;

  const geometry = circleGeoJSON.geometry || circleGeoJSON;
  const properties = circleGeoJSON.properties || {};
  if (!geometry || geometry.type !== "Point") return circleGeoJSON;

  const radius = Number(properties.radius);
  const center = Array.isArray(properties.center)
    ? properties.center
    : geometry.coordinates;

  if (!Number.isFinite(radius) || !Array.isArray(center) || center.length !== 2) {
    return circleGeoJSON;
  }

  const numPoints = 64;
  const coordinates: number[][] = [];
  for (let i = 0; i <= numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI;
    const lat = center[1] + (radius / 111320) * Math.cos(angle);
    const lng =
      center[0] +
      (radius / (111320 * Math.cos((center[1] * Math.PI) / 180))) * Math.sin(angle);
    coordinates.push([lng, lat]);
  }

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [coordinates],
    },
    properties: {
      ...properties,
      originalType: "circle",
      center,
      radius,
    },
  };
}
